import React, { useState, useRef, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, Lock, Pencil } from "lucide-react";
import { signup, loginWithCredentials, getIdToken, completeProfile, cancelSsoSignup, sendSignupOtp, verifySignupOtp, checkOrgDomain, checkOrgName } from "../../utils/keycloak";
import { useAuth } from "../../contexts/AuthContext";
import PolicyAcceptanceModal from "../policy/PolicyAcceptanceModal";
import { createCheckoutSession } from "../../services/paymentService";
import { PENDING_PLAN_KEY, PendingPlan } from "./PricingPage";
import { createLogger } from "../../utils/logger";
import { suggestOrgNameFromEmail, PUBLIC_EMAIL_PROVIDERS } from "../../utils/orgNameFromEmail";
import OrgSelect from "./OrgSelect";
import { getCountries, getCountryCallingCode, parsePhoneNumberFromString, type CountryCode } from "libphonenumber-js";
import "./Login.scss";

const logger = createLogger("Signup");

const EMAIL_REGEX = /\S+@\S+\.\S+/;

// Full ISO-3166 region list from libphonenumber-js (the same data Google's
// libphonenumber uses) — avoids hand-typing calling codes, which is both
// incomplete and typo-prone. Keyed by region (not dial code) since several
// countries share a calling code (+1: US, Canada, Jamaica, ...) — the actual
// "+<code>" is looked up from the selected region at submit time.
const countryRegionNames = new Intl.DisplayNames(["en"], { type: "region" });
function buildCountryOptions() {
  return getCountries()
    .map((region) => ({
      value: region as string,
      // Full "Country (+code)" for the open list (also what typeahead search matches
      // against); just "+code" for the closed trigger so the field stays compact.
      label: `${countryRegionNames.of(region) || region} (+${getCountryCallingCode(region)})`,
      triggerLabel: `+${getCountryCallingCode(region)}`,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

interface SignupProps {
  /**
   * Renders this form to finish setting up an already-authenticated,
   * SSO-created account (e.g. first Google login with no organization yet)
   * instead of the normal unauthenticated signup flow: email is pre-filled
   * and locked, no password fields, and submit hits /auth/complete-profile.
   */
  ssoCompletion?: boolean;
}

const Signup: React.FC<SignupProps> = ({ ssoCompletion = false }) => {
  const { refreshAuth, currentUser, token, logout } = useAuth();
  const [email, setEmail] = useState(ssoCompletion ? currentUser?.email || "" : "");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const COUNTRY_OPTIONS = useMemo(() => buildCountryOptions(), []);
  const [countryRegion, setCountryRegion] = useState<CountryCode>("US");
  const [phone, setPhone] = useState("");
  const [orgName, setOrgName] = useState("");
  // True while the Organization field still holds a value we guessed from the email
  // domain. Drives the greyed styling and the pencil.
  const [orgNameSuggested, setOrgNameSuggested] = useState(false);
  // Set the first time the user types in the field, and never unset. This — not
  // "the field is non-empty" — is what tells the suggestion effect to stand down:
  // someone who selects-all and deletes in order to type their own name leaves the
  // field empty for a moment, and inferring ownership from emptiness put the guess
  // straight back so they ended up with "ElastiqMy Org".
  const [orgNameTouched, setOrgNameTouched] = useState(false);
  const [orgNameFocused, setOrgNameFocused] = useState(false);
  // Non-null once /auth/check-org-domain confirms an organization already claims the
  // typed email's domain. Holds that org's real name, forces the Organization field to
  // it, and locks the field — typing a different name here would be discarded server
  // side anyway (see _resolve_signup_org's decision tree), so leaving it editable would
  // just mislead the applicant about what they're about to join.
  const [domainMatchedOrgName, setDomainMatchedOrgName] = useState<string | null>(null);
  // Non-null once /auth/check-org-name confirms that a personal-email applicant typed
  // the exact name of an org that already exists. Unlike domainMatchedOrgName, this
  // does NOT lock the field — the applicant picks what happens next (see the prompt
  // rendered below the field): request to join that real org, or keep going with their
  // own separate workspace under the name they typed.
  const [nameMatchedOrg, setNameMatchedOrg] = useState<string | null>(null);
  // The applicant's choice on the nameMatchedOrg prompt. Reset to null whenever the
  // match itself changes (org name edited again, or the match cleared) so a stale
  // choice from a previous match can never carry over silently.
  const [joinExistingOrgChoice, setJoinExistingOrgChoice] = useState(false);
  const [department, setDepartment] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [policyAgreed, setPolicyAgreed] = useState(false);
  const [policyModalOpen, setPolicyModalOpen] = useState(false);

  // Email OTP verification (self-service signup only — ssoCompletion already has a
  // verified email from the identity provider, so this step is skipped there).
  const [emailVerified, setEmailVerified] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [otpInfo, setOtpInfo] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);

  const navigate = useNavigate();
  // Prevents the already-logged-in effect from running during/after form submission
  const submitStarted = useRef(false);

  // Already-logged-in path: user landed on /signup while authenticated (e.g. from /pricing).
  // Doesn't apply in ssoCompletion mode — there, being logged in with no org is expected.
  React.useEffect(() => {
    if (ssoCompletion || !currentUser || submitStarted.current) return;

    const pendingRaw = localStorage.getItem(PENDING_PLAN_KEY);
    if (!pendingRaw) {
      navigate("/", { replace: true });
      return;
    }

    localStorage.removeItem(PENDING_PLAN_KEY);
    let parsed: PendingPlan;
    try {
      parsed = JSON.parse(pendingRaw);
    } catch {
      navigate("/", { replace: true });
      return;
    }

    getIdToken()
      .then(idToken => createCheckoutSession(idToken, parsed.plan_type, parsed.seat_count))
      .then(({ checkout_url }) => { window.location.href = checkout_url; })
      .catch(() => navigate("/", { replace: true }));
  }, [ssoCompletion, currentUser, navigate]);

  // Bailing on ssoCompletion (back button or "Log out") means the Keycloak account
  // the IdP broker auto-created on first SSO login is abandoned with no org — delete
  // it so the user isn't left with an orphaned account they can't see or remove.
  const abandonSsoSignup = async () => {
    try {
      const idToken = token || (await getIdToken());
      await cancelSsoSignup(idToken);
    } catch (err) {
      logger.error("Failed to cancel abandoned SSO signup", err);
    }
    await logout();
  };

  // ssoCompletion mode: pressing the browser back button before finishing this
  // form means the user is bailing on setup — log them out instead of leaving
  // them stuck mid-signup (OrganizationGuard would just show this same form on
  // whatever route "back" lands on, since organization_id is still unset).
  React.useEffect(() => {
    if (!ssoCompletion) return;

    const handlePopState = () => {
      if (!submitStarted.current) abandonSsoSignup();
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [ssoCompletion, logout]);

  // Tick down the resend cooldown once a code has been sent.
  React.useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const handleSendOtp = async () => {
    const trimmedEmail = email.trim();
    if (!EMAIL_REGEX.test(trimmedEmail)) { setOtpError("Enter a valid email address first."); return; }
    setOtpSending(true); setOtpError(""); setOtpInfo("");
    try {
      const { sent, message, expires_in_seconds } = await sendSignupOtp(trimmedEmail);
      if (sent) {
        setOtpSent(true);
        const minutes = Math.max(1, Math.round((expires_in_seconds || 600) / 60));
        setOtpInfo(`We emailed you a 6-digit code. It expires in ${minutes} minutes.`);
        setResendCooldown(30);
      } else {
        setOtpError(message || "Could not send the code. Please try again.");
      }
    } catch (err: unknown) {
      setOtpError(err instanceof Error ? err.message : "Could not send the code. Please try again.");
    } finally {
      setOtpSending(false);
    }
  };

  // Offer an organization name derived from a work email domain
  // (chirag@elastiq.ai -> "Elastiq"). Runs off the typed email rather than off OTP
  // verification so it is already filled in by the time the user scrolls to the field,
  // and so fixing a typo in the email fixes the guess too.
  //
  // Only ever writes to the field while it is empty or still holds a previous guess:
  // once the user edits it themselves the suggestion machinery stops touching it for
  // good. Consumer providers return null and leave the field alone entirely.
  useEffect(() => {
    if (orgNameTouched) return;
    // A confirmed domain match is the real org name, not a guess — the effect below
    // sets it and turns orgNameSuggested off in the same update, which would otherwise
    // re-fire this effect (orgNameSuggested is a dependency) and immediately overwrite
    // that real name with the email-derived title-case guess.
    if (domainMatchedOrgName) return;

    const trimmed = email.trim();
    const suggestion = EMAIL_REGEX.test(trimmed) ? suggestOrgNameFromEmail(trimmed) : null;

    if (suggestion) {
      setOrgName(prev => (prev === suggestion ? prev : suggestion));
      setOrgNameSuggested(true);
    } else if (orgNameSuggested) {
      // The email stopped yielding a guess (cleared, or edited to a consumer domain) —
      // take our own suggestion back out rather than leaving a stale one behind for an
      // address it has nothing to do with.
      setOrgName("");
      setOrgNameSuggested(false);
    }
  }, [email, orgNameTouched, orgNameSuggested, domainMatchedOrgName]);

  // Debounced check for whether this email's domain is already claimed by a real
  // organization. Overrides the title-cased guess above (and anything the applicant
  // typed) once a match comes back — see domainMatchedOrgName's comment for why
  // locking beats leaving it editable. Debounced (rather than firing per keystroke)
  // and guarded with a `cancelled` flag so a fast typist's earlier, slower request
  // can't resolve after a later one and stomp its result.
  useEffect(() => {
    const trimmed = email.trim();
    if (!EMAIL_REGEX.test(trimmed)) {
      setDomainMatchedOrgName(null);
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const result = await checkOrgDomain(trimmed);
        if (cancelled) return;
        if (result.matched && result.organization_name) {
          setDomainMatchedOrgName(result.organization_name);
          setOrgName(result.organization_name);
          setOrgNameSuggested(false);
        } else {
          setDomainMatchedOrgName(null);
        }
      } catch {
        if (!cancelled) setDomainMatchedOrgName(null);
      }
    }, 500);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [email]);

  // Debounced check for whether a *personal*-email applicant's typed org name already
  // belongs to a real org. Gated client-side on PUBLIC_EMAIL_PROVIDERS (mirrored by the
  // backend's own PUBLIC_EMAIL_DOMAINS gate) so a corporate email never fires this or
  // shows the prompt — that case is unaffected on purpose, see _find_or_create_trial_org.
  useEffect(() => {
    const trimmedEmail = email.trim().toLowerCase();
    const at = trimmedEmail.lastIndexOf("@");
    const domain = at > 0 ? trimmedEmail.slice(at + 1) : "";
    const trimmedName = orgName.trim();

    if (!PUBLIC_EMAIL_PROVIDERS.has(domain) || !trimmedName) {
      setNameMatchedOrg(null);
      setJoinExistingOrgChoice(false);
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const result = await checkOrgName(trimmedEmail, trimmedName);
        if (cancelled) return;
        if (result.matched && result.organization_name) {
          setNameMatchedOrg(result.organization_name);
        } else {
          setNameMatchedOrg(null);
        }
        setJoinExistingOrgChoice(false);
      } catch {
        if (!cancelled) {
          setNameMatchedOrg(null);
          setJoinExistingOrgChoice(false);
        }
      }
    }, 500);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [email, orgName]);

  const handleVerifyOtp = async () => {
    const trimmedEmail = email.trim();
    const code = otpCode.trim();
    if (code.length !== 6) { setOtpError("Enter the 6-digit code."); return; }
    setOtpVerifying(true); setOtpError(""); setOtpInfo("");
    try {
      const { verified, message } = await verifySignupOtp(trimmedEmail, code);
      if (verified) {
        setEmailVerified(true);
        setOtpInfo("");
      } else {
        setOtpError(message || "That code is invalid or has expired.");
      }
    } catch (err: unknown) {
      setOtpError(err instanceof Error ? err.message : "Verification failed. Please try again.");
    } finally {
      setOtpVerifying(false);
    }
  };

  const validate = (): boolean => {
    const errors: Record<string, string> = {};
    if (!ssoCompletion) {
      if (!email.trim()) errors.email = "Email is required";
      else if (!EMAIL_REGEX.test(email)) errors.email = "Please enter a valid email address";
      else if (!emailVerified) errors.email = "Please verify your email with the code we sent";
    }
    if (!firstName.trim()) errors.firstName = "First name is required";
    if (!lastName.trim()) errors.lastName = "Last name is required";
    if (!orgName.trim()) errors.orgName = "Organization/District is required";
    if (!phone.trim()) errors.phone = "Phone number is required";
    else {
      const parsed = parsePhoneNumberFromString(phone.trim(), countryRegion);
      if (!parsed?.isValid()) {
        errors.phone = "Please enter a valid phone number for the selected country";
      }
    }
    if (!password) errors.password = "Password is required";
    else if (password.length < 8) errors.password = "Password must be at least 8 characters";
    if (!confirmPassword) errors.confirmPassword = "Please confirm your password";
    else if (password !== confirmPassword) errors.confirmPassword = "Passwords do not match";
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!validate()) return;

    submitStarted.current = true;
    setLoading(true);

    // validate() above already confirmed this parses for the selected country.
    const fullPhone = parsePhoneNumberFromString(phone.trim(), countryRegion)!.format("E.164");

    if (ssoCompletion) {
      try {
        const idToken = token || (await getIdToken());
        await completeProfile({
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          phone: fullPhone,
          organization_name: orgName.trim(),
          department: department.trim() || undefined,
          password,
          confirm_password: confirmPassword,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }, idToken);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : "";
        setError(message || "Something went wrong. Please try again.");
        setLoading(false);
        return;
      }
      await refreshAuth();
      navigate("/", { replace: true });
      setLoading(false);
      return;
    }

    // Phase 1: create account + log in
    try {
      await signup({
        email: email.trim(),
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        phone: fullPhone,
        organization_name: orgName.trim(),
        department: department.trim() || undefined,
        password,
        confirm_password: confirmPassword,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        join_existing_org: !!nameMatchedOrg && joinExistingOrgChoice,
      });
      await loginWithCredentials(email.trim(), password);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "";
      if (message.toLowerCase().includes("account with this email")) {
        setError("An account with this email already exists. Please sign in instead.");
      } else {
        setError(message || "Something went wrong. Please try again.");
      }
      setLoading(false);
      return;
    }

    // Phase 2: checkout if a plan was selected, otherwise normal auth
    const pendingRaw = localStorage.getItem(PENDING_PLAN_KEY);
    if (pendingRaw) {
      localStorage.removeItem(PENDING_PLAN_KEY);
      try {
        const { plan_type, seat_count }: PendingPlan = JSON.parse(pendingRaw);
        const idToken = await getIdToken();
        const { checkout_url } = await createCheckoutSession(idToken, plan_type, seat_count);
        window.location.href = checkout_url;
        return;
      } catch {
        setError("Your account was created, but checkout failed. Please visit the Pricing page to subscribe.");
        setLoading(false);
        return;
      }
    }

    // No plan selected: refresh auth then let FTUEGuard redirect to /getting-started if needed
    await refreshAuth();
    navigate("/", { replace: true });
    setLoading(false);
  };

  // The greyed text + pencil are the "this is a guess" affordance, so they clear the
  // moment the field has focus — while someone is actively editing it, it should look
  // like any other input they are typing in.
  const showSuggestionHint = orgNameSuggested && !orgNameFocused;
  const showDomainLockHint = !!domainMatchedOrgName;

  return (
    <div className="login-page">
      <div className="login-card" style={{ maxWidth: 440 }}>
        <div className="login-card-header">
          <img src="/assets/branding/rc-logo-dark.png" alt="Reality Coach" className="login-logo" />
          <h1 className="login-title">{ssoCompletion ? "Complete your account" : "Create your account"}</h1>
          <p className="login-subtitle">
            {ssoCompletion ? "Just a few more details to get started" : "Start your 14-day free trial"}
          </p>
        </div>

        {error && <div className="login-error" role="alert">{error}</div>}

        <form className="login-form" onSubmit={handleSubmit}>
          <div className="login-field">
            <label htmlFor="email" className="login-label">Email {!ssoCompletion && <span style={{ color: "#ef4444" }}>*</span>}</label>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <input
                id="email"
                type="email"
                className={`login-input${fieldErrors.email ? " login-input--error" : ""}`}
                placeholder="you@example.com"
                value={email}
                onChange={(e) => {
                  const val = e.target.value;
                  setEmail(val);
                  // Editing the email invalidates any prior verification.
                  if (emailVerified || otpSent) {
                    setEmailVerified(false);
                    setOtpSent(false);
                    setOtpCode("");
                    setOtpError("");
                    setOtpInfo("");
                  }
                }}
                disabled={loading || ssoCompletion}
                aria-invalid={!!fieldErrors.email}
                style={{ flex: 1, ...(emailVerified ? { backgroundColor: "#f0fdf4", borderColor: "#86efac" } : {}) }}
              />
              {!ssoCompletion && !emailVerified && (
                <button
                  type="button"
                  onClick={handleSendOtp}
                  disabled={otpSending || loading || !EMAIL_REGEX.test(email.trim()) || resendCooldown > 0}
                  style={{
                    whiteSpace: "nowrap", padding: "0 0.875rem", borderRadius: "6px",
                    border: "1px solid #4F99BD", background: "#fff", color: "#005581",
                    fontSize: "0.85rem", fontWeight: 600,
                    cursor: (otpSending || resendCooldown > 0) ? "default" : "pointer",
                    opacity: (otpSending || loading || !EMAIL_REGEX.test(email.trim()) || resendCooldown > 0) ? 0.6 : 1,
                  }}
                >
                  {otpSending ? "Sending…" : otpSent ? (resendCooldown > 0 ? `Resend (${resendCooldown}s)` : "Resend") : "Send code"}
                </button>
              )}
            </div>

            {!ssoCompletion && emailVerified && (
              <p style={{ fontSize: "0.8rem", color: "#16a34a", margin: "0.4rem 0 0", fontWeight: 600 }}>
                ✓ Email verified
              </p>
            )}

            {!ssoCompletion && otpSent && !emailVerified && (
              <div style={{ marginTop: "0.625rem", padding: "0.75rem", background: "#f0f7fb", border: "1px solid #d6e8f2", borderRadius: "8px" }}>
                <label style={{ fontSize: "0.8rem", marginBottom: "0.4rem", display: "block", color: "#374151" }}>
                  Enter the 6-digit code we emailed you
                </label>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="123456"
                    disabled={otpVerifying}
                    className="login-input"
                    style={{ flex: 1, letterSpacing: "0.3em", fontWeight: 600 }}
                  />
                  <button
                    type="button"
                    onClick={handleVerifyOtp}
                    disabled={otpVerifying || otpCode.length !== 6}
                    style={{
                      whiteSpace: "nowrap", padding: "0 1rem", borderRadius: "6px", border: "none",
                      background: "linear-gradient(135deg, #4F99BD, #005581)", color: "#fff",
                      fontSize: "0.85rem", fontWeight: 600,
                      cursor: (otpVerifying || otpCode.length !== 6) ? "default" : "pointer",
                      opacity: (otpVerifying || otpCode.length !== 6) ? 0.6 : 1,
                    }}
                  >
                    {otpVerifying ? "Verifying…" : "Verify"}
                  </button>
                </div>
              </div>
            )}

            {otpError && <p style={{ fontSize: "0.8rem", color: "#dc2626", margin: "0.4rem 0 0" }}>{otpError}</p>}
            {otpInfo && !otpError && <p style={{ fontSize: "0.8rem", color: "#6b7280", margin: "0.4rem 0 0" }}>{otpInfo}</p>}

            {fieldErrors.email && <div className="login-field-error">{fieldErrors.email}</div>}
          </div>

          <div style={{ display: "flex", gap: "0.75rem" }}>
            <div className="login-field" style={{ flex: 1 }}>
              <label htmlFor="firstName" className="login-label">First <span style={{ color: "#ef4444" }}>*</span></label>
              <input
                id="firstName"
                type="text"
                className={`login-input${fieldErrors.firstName ? " login-input--error" : ""}`}
                placeholder="Jane"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={loading}
                aria-invalid={!!fieldErrors.firstName}
              />
              {fieldErrors.firstName && <div className="login-field-error">{fieldErrors.firstName}</div>}
            </div>

            <div className="login-field" style={{ flex: 1 }}>
              <label htmlFor="lastName" className="login-label">Last <span style={{ color: "#ef4444" }}>*</span></label>
              <input
                id="lastName"
                type="text"
                className={`login-input${fieldErrors.lastName ? " login-input--error" : ""}`}
                placeholder="Smith"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={loading}
                aria-invalid={!!fieldErrors.lastName}
              />
              {fieldErrors.lastName && <div className="login-field-error">{fieldErrors.lastName}</div>}
            </div>
          </div>

          <div className="login-field">
            <label htmlFor="phone" className="login-label">Phone <span style={{ color: "#ef4444" }}>*</span></label>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <div style={{ width: "6rem", flexShrink: 0 }}>
                <OrgSelect
                  value={countryRegion}
                  options={COUNTRY_OPTIONS}
                  onChange={(v) => setCountryRegion(v as CountryCode)}
                  disabled={loading}
                  ariaLabel="Country code"
                  triggerStyle={{
                    width: "100%",
                    padding: "0.625rem 0.75rem",
                    border: "1px solid #d1d5db",
                    borderRadius: "8px",
                    fontSize: "0.875rem",
                    color: "#1e293b",
                    background: "#fff",
                    cursor: "pointer",
                  }}
                />
              </div>
              <input
                id="phone"
                type="tel"
                className={`login-input${fieldErrors.phone ? " login-input--error" : ""}`}
                style={{ flex: 1 }}
                placeholder="(555) 000-0000"
                value={phone}
                onChange={(e) => {
                  const val = e.target.value;
                  // A pasted full international number (e.g. "+91 70376 52374") both
                  // auto-selects the right country and strips the dial code into the
                  // national-number field, instead of being rejected outright.
                  const pastedIntl = val.trim().startsWith("+") ? parsePhoneNumberFromString(val) : undefined;
                  if (pastedIntl?.country) {
                    setCountryRegion(pastedIntl.country);
                    setPhone(pastedIntl.nationalNumber);
                  } else if (val === "" || /^[0-9\-\s()]*$/.test(val)) {
                    setPhone(val);
                  }
                }}
                disabled={loading}
                aria-invalid={!!fieldErrors.phone}
              />
            </div>
            {fieldErrors.phone && <div className="login-field-error">{fieldErrors.phone}</div>}
          </div>

          <div className="login-field">
            <label htmlFor="orgName" className="login-label">Organization/District <span style={{ color: "#ef4444" }}>*</span></label>
            <div style={{ position: "relative" }}>
              <input
                id="orgName"
                type="text"
                className={`login-input${fieldErrors.orgName ? " login-input--error" : ""}`}
                placeholder="Boys Town"
                value={orgName}
                onChange={(e) => {
                  // Any edit hands the field over to the user for good, including an
                  // edit that empties it — the suggestion effect checks `touched`
                  // before it ever writes again.
                  setOrgNameTouched(true);
                  setOrgNameSuggested(false);
                  setOrgName(e.target.value);
                }}
                onFocus={() => setOrgNameFocused(true)}
                onBlur={() => setOrgNameFocused(false)}
                disabled={loading || showDomainLockHint}
                aria-invalid={!!fieldErrors.orgName}
                aria-describedby={showDomainLockHint ? "orgName-domain-locked" : showSuggestionHint ? "orgName-suggested" : undefined}
                style={
                  showDomainLockHint || showSuggestionHint
                    ? {
                        // Same grey as the placeholder (#64748b, 4.76:1 — AA for text) — a suggestion (or a
                        // locked, already-resolved value) should read as visibly not
                        // freely-typed, the way placeholder text does.
                        color: "#64748b",
                        paddingRight: "2.25rem",
                      }
                    : undefined
                }
              />
              {showDomainLockHint ? (
                <Lock
                  size={14}
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    right: "0.75rem",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#94a3b8",
                    pointerEvents: "none",
                  }}
                />
              ) : showSuggestionHint && (
                <Pencil
                  size={14}
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    right: "0.75rem",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#94a3b8",
                    pointerEvents: "none",
                  }}
                />
              )}
            </div>
            {showDomainLockHint ? (
              <div
                id="orgName-domain-locked"
                style={{ marginTop: "0.3rem", fontSize: "0.75rem", color: "#6b7280", lineHeight: 1.5 }}
              >
                This organization already exists — you'll join it when you sign up.
              </div>
            ) : showSuggestionHint && (
              <div
                id="orgName-suggested"
                style={{ marginTop: "0.3rem", fontSize: "0.75rem", color: "#6b7280", lineHeight: 1.5 }}
              >
                Suggested from your email — edit it if your organization is named differently.
              </div>
            )}
            {fieldErrors.orgName && <div className="login-field-error">{fieldErrors.orgName}</div>}

            {nameMatchedOrg && (
              <div
                role="group"
                aria-label="Organization already exists"
                style={{
                  marginTop: "0.5rem",
                  padding: "0.75rem",
                  background: "#fffbeb",
                  border: "1px solid #fde68a",
                  borderRadius: "8px",
                }}
              >
                <p style={{ margin: 0, fontSize: "0.8rem", color: "#78350f", lineHeight: 1.5 }}>
                  An organization named "{nameMatchedOrg}" already exists. If you want to join this
                  existing organization, select "Request to Join"; otherwise select "Create Separate
                  Organization".
                </p>
                <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.6rem" }}>
                  <button
                    type="button"
                    onClick={() => setJoinExistingOrgChoice(true)}
                    disabled={loading}
                    style={{
                      padding: "0.4rem 0.75rem",
                      borderRadius: "6px",
                      fontSize: "0.8rem",
                      fontWeight: 600,
                      cursor: loading ? "default" : "pointer",
                      border: joinExistingOrgChoice ? "1px solid #005581" : "1px solid #d1d5db",
                      background: joinExistingOrgChoice ? "#005581" : "#fff",
                      color: joinExistingOrgChoice ? "#fff" : "#374151",
                    }}
                  >
                    Request to Join
                  </button>
                  <button
                    type="button"
                    onClick={() => setJoinExistingOrgChoice(false)}
                    disabled={loading}
                    style={{
                      padding: "0.4rem 0.75rem",
                      borderRadius: "6px",
                      fontSize: "0.8rem",
                      fontWeight: 600,
                      cursor: loading ? "default" : "pointer",
                      border: !joinExistingOrgChoice ? "1px solid #005581" : "1px solid #d1d5db",
                      background: !joinExistingOrgChoice ? "#005581" : "#fff",
                      color: !joinExistingOrgChoice ? "#fff" : "#374151",
                    }}
                  >
                    Create Separate Organization
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="login-field">
            <label htmlFor="department" className="login-label">Department/School <span style={{ color: "#6b7280", fontSize: "0.8125rem" }}>(optional)</span></label>
            <input
              id="department"
              type="text"
              className="login-input"
              placeholder="Lincoln Elementary"
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              disabled={loading}
            />
          </div>

          <div className="login-field">
            <label htmlFor="password" className="login-label">Password <span style={{ color: "#ef4444" }}>*</span></label>
            <div className="password-input-wrapper">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                className={`login-input${fieldErrors.password ? " login-input--error" : ""}`}
                placeholder="Min. 8 characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                aria-invalid={!!fieldErrors.password}
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {ssoCompletion && !fieldErrors.password && (
              <div style={{ fontSize: "0.75rem", color: "#6b7280", marginTop: "0.25rem" }}>
                So you can also sign in with your email and password later.
              </div>
            )}
            {fieldErrors.password && <div className="login-field-error">{fieldErrors.password}</div>}
          </div>

          <div className="login-field">
            <label htmlFor="confirmPassword" className="login-label">Confirm Password <span style={{ color: "#ef4444" }}>*</span></label>
            <div className="password-input-wrapper">
              <input
                id="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                className={`login-input${fieldErrors.confirmPassword ? " login-input--error" : ""}`}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={loading}
                aria-invalid={!!fieldErrors.confirmPassword}
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                tabIndex={-1}
                aria-label={showConfirmPassword ? "Hide password" : "Show password"}
              >
                {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {fieldErrors.confirmPassword && (
              <div className="login-field-error">{fieldErrors.confirmPassword}</div>
            )}
          </div>

          <div className="login-field" style={{ gap: 0 }}>
            <label style={{ display: "flex", alignItems: "flex-start", gap: "0.5rem", fontSize: "0.8125rem", color: "#374151", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={policyAgreed}
                onChange={(e) => {
                  if (e.target.checked) {
                    setPolicyModalOpen(true);
                  } else {
                    setPolicyAgreed(false);
                  }
                }}
                disabled={loading}
                style={{ marginTop: "0.15rem", flexShrink: 0 }}
              />
              <span>
                I have read and agree to the{" "}
                <button
                  type="button"
                  className="login-forgot-link"
                  style={{ background: "none", border: "none", padding: 0, font: "inherit", cursor: "pointer" }}
                  onClick={() => setPolicyModalOpen(true)}
                >
                  Terms of Use
                </button>
                {" "}(Privacy Policy &amp; EULA)
              </span>
            </label>
          </div>

          <button type="submit" className="login-btn login-btn--primary" disabled={loading || !policyAgreed}>
            {loading ? <span className="login-spinner" /> : ssoCompletion ? "Finish setup" : "Create account"}
          </button>

          {!ssoCompletion && (
            <p style={{ textAlign: "center", fontSize: "0.8125rem", color: "#64748b", margin: 0 }}>
              Already have an account?{" "}
              <Link to="/login" className="login-forgot-link">Sign in</Link>
            </p>
          )}

          {ssoCompletion && (
            <p style={{ textAlign: "center", fontSize: "0.8125rem", color: "#64748b", margin: 0 }}>
              Not you?{" "}
              <button
                type="button"
                className="login-forgot-link"
                style={{ background: "none", border: "none", padding: 0, font: "inherit", cursor: "pointer" }}
                onClick={() => abandonSsoSignup()}
                disabled={loading}
              >
                Log out
              </button>
            </p>
          )}
        </form>
      </div>

      <PolicyAcceptanceModal
        isOpen={policyModalOpen}
        onAccept={() => { setPolicyAgreed(true); setPolicyModalOpen(false); }}
        onLogout={() => setPolicyModalOpen(false)}
        isSaving={false}
        cancelLabel="Cancel"
      />
    </div>
  );
};

export default Signup;
